"use client";

import {
	type ColumnDef,
	flexRender,
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	type OnChangeFn,
	type PaginationState,
	type Row,
	type RowData,
	type RowSelectionState,
	type SortingState,
	useReactTable,
} from "@tanstack/react-table";
import {
	ArrowLeft2,
	ArrowRight2,
	SearchNormal1,
} from "iconsax-react";
import { Fragment, type ReactNode, useEffect, useMemo, useState } from "react";
import { PAGE_SIZE_OPTIONS, rowRange } from "../hooks/offset-list-params";
import { cn } from "../utils";
import { Button } from "./button";
import {
	HeaderContent,
	isPinnedRight,
	pinClass,
	resolveTableColumns,
	shouldTruncate,
	SkeletonRows,
	truncateTitle,
} from "./data-table-parts";
import { Input } from "./input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./select";
import {
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./table";

declare module "@tanstack/react-table" {
	// biome-ignore lint/correctness/noUnusedVariables: column meta extension
	interface ColumnMeta<TData extends RowData, TValue> {
		align?: "left" | "right";
		pin?: "left" | "right";
		truncate?: boolean;
	}
}

const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_COLUMN_SIZE = 160;
export interface DataTableProps<TData, TValue> {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];

	pageSizeOptions?: number[];
	defaultPageSize?: number;

	pageCount?: number;
	rowCount?: number;
	pagination?: PaginationState;
	onPaginationChange?: OnChangeFn<PaginationState>;

	sorting?: SortingState;
	onSortingChange?: OnChangeFn<SortingState>;

	globalFilter?: string;
	onGlobalFilterChange?: (value: string) => void;
	enableSearch?: boolean;
	searchPlaceholder?: string;

	enableRowSelection?: boolean;
	getRowId?: (row: TData) => string;
	onSelectionChange?: (selectedIds: string[]) => void;

	onRowClick?: (row: TData) => void;
	renderExpandedRow?: (row: Row<TData>) => ReactNode;
	getRowCanExpand?: (row: Row<TData>) => boolean;

	toolbar?: ReactNode;
	emptyMessage?: string;
	/** Rendered in place of `emptyMessage` when the list comes back empty. */
	emptyState?: ReactNode;
	caption?: string;
	isLoading?: boolean;
	isFetching?: boolean;
}

export function DataTable<TData, TValue>({
	columns,
	data,
	pageSizeOptions = [...PAGE_SIZE_OPTIONS],
	defaultPageSize = DEFAULT_PAGE_SIZE,
	pageCount,
	rowCount,
	pagination: paginationProp,
	onPaginationChange,
	sorting: sortingProp,
	onSortingChange,
	globalFilter: globalFilterProp,
	onGlobalFilterChange,
	enableSearch = true,
	searchPlaceholder = "Search...",
	enableRowSelection,
	getRowId,
	onSelectionChange,
	onRowClick,
	renderExpandedRow,
	getRowCanExpand,
	toolbar,
	emptyMessage = "No results.",
	emptyState,
	caption,
	isLoading,
	isFetching,
}: DataTableProps<TData, TValue>) {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [globalFilter, setGlobalFilter] = useState("");
	const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
	const [expanded, setExpanded] = useState({});

	const isManualPagination = paginationProp !== undefined;
	const isManualSort = sortingProp !== undefined;
	const isManualFilter = globalFilterProp !== undefined;

	const resolvedSorting = isManualSort ? sortingProp : sorting;
	const resolvedFilter = isManualFilter ? (globalFilterProp ?? "") : globalFilter;

	const resolvedColumns = useMemo(
		() => resolveTableColumns(columns, enableRowSelection),
		[columns, enableRowSelection],
	);

	const table = useReactTable({
		data,
		columns: resolvedColumns,
		defaultColumn: {
			enableSorting: !isManualPagination,
			maxSize: 480,
			minSize: 48,
			size: DEFAULT_COLUMN_SIZE,
		},
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: renderExpandedRow ? getExpandedRowModel() : undefined,
		...(isManualPagination
			? {
					manualPagination: true,
					pageCount: pageCount ?? -1,
					rowCount,
				}
			: { getPaginationRowModel: getPaginationRowModel() }),
		...(isManualSort
			? { manualSorting: true, enableSortingRemoval: true }
			: { getSortedRowModel: getSortedRowModel() }),
		...(isManualFilter
			? { manualFiltering: true, enableFilters: false }
			: { getFilteredRowModel: getFilteredRowModel() }),
		getRowCanExpand,
		getRowId: getRowId ? (row) => getRowId(row as TData) : undefined,
		enableRowSelection,
		onRowSelectionChange: setRowSelection,
		onSortingChange: isManualSort ? onSortingChange : setSorting,
		onGlobalFilterChange: isManualFilter
			? (updater) => {
					const next =
						typeof updater === "function" ? updater(resolvedFilter) : updater;
					onGlobalFilterChange?.(next);
				}
			: setGlobalFilter,
		onPaginationChange,
		onExpandedChange: setExpanded,
		state: {
			sorting: resolvedSorting,
			globalFilter: resolvedFilter,
			rowSelection,
			expanded,
			...(paginationProp ? { pagination: paginationProp } : {}),
		},
		initialState: isManualPagination
			? undefined
			: { pagination: { pageSize: defaultPageSize } },
	});

	useEffect(() => {
		if (!enableRowSelection || !onSelectionChange) return;
		onSelectionChange(Object.keys(rowSelection).filter((k) => rowSelection[k]));
	}, [rowSelection, enableRowSelection, onSelectionChange]);

	const filterValue = resolvedFilter;
	const onFilterInput = (value: string) => {
		if (isManualFilter) {
			onGlobalFilterChange?.(value);
		} else {
			setGlobalFilter(value);
			table.setPageIndex(0);
		}
	};

	const pageState = table.getState().pagination;
	const total = rowCount ?? table.getFilteredRowModel().rows.length;
	const range = rowRange(pageState.pageIndex, pageState.pageSize, total);
	const showPagination =
		isManualPagination || table.getPageCount() > 1 || total > pageState.pageSize;
	const colCount = resolvedColumns.length;
	const rows = table.getRowModel().rows;

	return (
		<div className="flex min-w-0 flex-col">
			{(enableSearch || toolbar) && (
				<div className="flex flex-wrap items-center gap-2 pb-3">
					{enableSearch && (
						<div className="relative min-w-0 max-w-sm flex-1">
							<SearchNormal1
								className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground"
								color="currentColor"
								variant="Linear"
							/>
							<Input
								value={filterValue}
								onChange={(e) => onFilterInput(e.target.value)}
								placeholder={searchPlaceholder}
								className="h-8 pl-8 text-sm"
							/>
						</div>
					)}
					{toolbar}
				</div>
			)}
			<div
				className={cn(
					"max-h-[calc(100svh-14rem)] min-w-0 overflow-auto rounded-md border",
					isFetching && !isLoading && "opacity-70",
				)}
			>
				<table
					className="w-full caption-bottom text-sm"
					data-slot="table"
					style={{ minWidth: table.getTotalSize() }}
				>
					{caption ? <caption className="sr-only">{caption}</caption> : null}
					<TableHeader className="sticky top-0 z-20 bg-background">
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id} className="hover:bg-transparent">
								{headerGroup.headers.map((header) => (
									<TableHead
										key={header.id}
										className={cn(
											header.column.columnDef.meta?.align === "right" && "text-right",
											pinClass(header.column),
										)}
										style={{ width: header.getSize() }}
									>
										{header.isPlaceholder ? null : (
											<HeaderContent header={header} />
										)}
									</TableHead>
								))}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{isLoading ? (
							<SkeletonRows columns={colCount} />
						) : rows.length ? (
							rows.map((row) => (
								<Fragment key={row.id}>
									<TableRow
										data-state={row.getIsSelected() ? "selected" : undefined}
										className={onRowClick ? "cursor-pointer" : undefined}
										onClick={() => onRowClick?.(row.original)}
									>
										{row.getVisibleCells().map((cell) => {
											const truncate = shouldTruncate(cell.column);
											return (
												<TableCell
													key={cell.id}
													className={cn(
														cell.column.columnDef.meta?.align === "right" &&
															"text-right tabular-nums",
														pinClass(cell.column),
														truncate && "max-w-0",
													)}
													style={{ width: cell.column.getSize() }}
												>
													<div
														className={truncate ? "min-w-0 truncate" : undefined}
														title={truncateTitle(cell)}
													>
														{flexRender(
															cell.column.columnDef.cell,
															cell.getContext(),
														)}
													</div>
												</TableCell>
											);
										})}
									</TableRow>
									{row.getIsExpanded() && renderExpandedRow ? (
										<TableRow className="bg-muted/30 hover:bg-muted/30">
											<TableCell colSpan={colCount} className="p-0">
												{renderExpandedRow(row)}
											</TableCell>
										</TableRow>
									) : null}
								</Fragment>
							))
						) : (
							<TableRow>
								<TableCell colSpan={colCount} className="h-24 text-center">
									{emptyState ?? emptyMessage}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</table>
			</div>
			{showPagination ? (
				<div className="flex flex-wrap items-center justify-between gap-2 py-3">
					<div className="flex items-center gap-2">
						<p className="text-muted-foreground text-sm">Rows per page</p>
						<Select
							value={`${pageState.pageSize}`}
							onValueChange={(value) => table.setPageSize(Number(value))}
						>
							<SelectTrigger size="sm" className="w-20">
								<SelectValue />
							</SelectTrigger>
							{/* item-aligned (the default) pins the list flush above the trigger
							    at the viewport bottom, so the selected size renders twice —
							    once in the list, once on the trigger — reading as a
							    duplicated option. Popper detaches the list upward instead. */}
							<SelectContent position="popper" side="top">
								{pageSizeOptions.map((size) => (
									<SelectItem key={size} value={`${size}`}>
										{size}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className="flex items-center gap-2">
						<p className="text-muted-foreground text-sm tabular-nums">
							{range.total === 0
								? "0 of 0"
								: `${range.start}–${range.end} of ${range.total.toLocaleString()}`}
						</p>
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.previousPage()}
							disabled={!table.getCanPreviousPage()}
						>
							<ArrowLeft2 className="size-4" color="currentColor" variant="Linear" />
							Previous
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => table.nextPage()}
							disabled={!table.getCanNextPage()}
						>
							Next
							<ArrowRight2 className="size-4" color="currentColor" variant="Linear" />
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
}
